import type { Vec2, Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { loftSolid, type Ring3 } from './shipSolids';

/*
 * Small closed timbers for ship fittings. Each is lofted, so its faces carry metric plank
 * coordinates like the hull's, rather than the unit square MeshBuilder.box gives every face.
 */

/** A box on the ship's axes. */
export function timberBox(builder: MeshBuilder, style: VertexStyle, min: Vec3, max: Vec3): void {
  const ring = (x: number): Ring3 => [
    [x, min[1], max[2]],
    [x, max[1], max[2]],
    [x, max[1], min[2]],
    [x, min[1], min[2]],
  ];
  loftSolid(builder, style, [ring(min[0]), ring(max[0])]);
}

/**
 * A beam of `width` across and `height` up whose section is centred on the line from `from` to
 * `to`; the line runs level or near it, and the section stays upright.
 */
export function timberBeam(
  builder: MeshBuilder,
  style: VertexStyle,
  from: Vec3,
  to: Vec3,
  width: number,
  height: number
): void {
  const [dx, dz] = [to[0] - from[0], to[2] - from[2]];
  const run = Math.hypot(dx, dz);
  if (!(run > 1e-6)) throw new Error('A beam needs a horizontal run');
  const [nx, nz] = [(-dz / run) * (width / 2), (dx / run) * (width / 2)];
  const ring = (c: Vec3): Ring3 => [
    [c[0] + nx, c[1] - height / 2, c[2] + nz],
    [c[0] + nx, c[1] + height / 2, c[2] + nz],
    [c[0] - nx, c[1] + height / 2, c[2] - nz],
    [c[0] - nx, c[1] - height / 2, c[2] - nz],
  ];
  loftSolid(builder, style, [ring(from), ring(to)]);
}

/** An upright square post standing on `base`. */
export function timberPost(
  builder: MeshBuilder,
  style: VertexStyle,
  base: Vec3,
  height: number,
  size: number
): void {
  const a = size / 2;
  const ring = (y: number): Ring3 => [
    [base[0] + a, y, base[2] - a],
    [base[0] + a, y, base[2] + a],
    [base[0] - a, y, base[2] + a],
    [base[0] - a, y, base[2] - a],
  ];
  loftSolid(builder, style, [ring(base[1]), ring(base[1] + height)]);
}

/** A strip's outer face patterned apart from the rest: its style, and the coordinates of its
    bottom and top edges at each sample. */
export interface OuterFace {
  readonly style: VertexStyle;
  readonly uv: (sample: number, top: boolean) => Vec2;
}

/**
 * A strip along one side of a ship through `samples` along its length, its starboard section
 * given at each sample as outer bottom, outer top, inner top, inner bottom; the port strip
 * mirrors it and runs its ring the other way round, so both face outward. `outer` styles the
 * outer faces of both.
 */
export function sideStrips(
  builder: MeshBuilder,
  style: VertexStyle,
  samples: readonly number[],
  section: (s: number, last: boolean) => readonly [Vec3, Vec3, Vec3, Vec3],
  outer?: OuterFace
): void {
  const starboard = samples.map((s, i) => section(s, i === samples.length - 1));
  const mirror = ([x, y, z]: Vec3): Vec3 => [x, y, -z];
  const faceStyle = (k: number): VertexStyle => (outer && k === 0 ? outer.style : style);
  loftSolid(builder, style, starboard, faceStyle, (r, k) => outer!.uv(r, k === 1));
  loftSolid(
    builder,
    style,
    starboard.map(([ob, ot, it, ib]) => [mirror(ot), mirror(ob), mirror(ib), mirror(it)]),
    faceStyle,
    (r, k) => outer!.uv(r, k === 0)
  );
}
